import { Plan, PlanTierIndex } from "@jugl-web/rest-api";
import { TFunction, useTranslations } from "@jugl-web/utils";
import { useMemo } from "react";
import {
  PAID_PLAN_TIERS,
  getPlanTierIndex,
} from "@web-src/modules/cx/pages/CXPlansPage/utils/planTierUtils";

const ADDON_ID_USERS = "jugl::users";
const ADDON_ID_AI_CREDITS = "jugl::ai_credits";
const ADDON_ID_KNOWLEDGE = "jugl::knowledge";
// Tickets currently reuse the `jugl::tasks` addon — there is no dedicated
// `jugl::tickets` key yet (kept in sync with `useTicketsLimit`).
const ADDON_ID_TICKETS = "jugl::tasks";

const getAddonCredits = (plan: Plan, addonId: string): number | null =>
  plan.default_addons.find((a) => a.id === addonId)?.credits ?? null;

const formatCredits = (credits: number): string =>
  credits >= 1000 ? credits.toLocaleString("en-US") : String(credits);

export type PlanFeatureCounts = {
  userCredits: number;
  aiCredits: number;
  knowledgeCredits: number;
  /** Ticket allowance; `null` means unlimited. */
  ticketCredits: number | null;
};

interface PlanFeatureDefinition {
  name: string;
  tooltip?: string;
  visibleOn?: readonly PlanTierIndex[];
}

const buildPlanFeatures = (
  t: TFunction,
  {
    userCredits,
    aiCredits,
    knowledgeCredits,
    ticketCredits,
  }: PlanFeatureCounts,
  planTier: PlanTierIndex
): PlanFeatureDefinition[] => {
  const features: PlanFeatureDefinition[] = [
    {
      name: t(
        {
          id: "plans-data.features.user-count",
          defaultMessage: "{count} {count, plural, one {user} other {users} }",
        },
        { count: userCredits }
      ),
    },
    {
      name: t(
        {
          id: "plans-data.features.message-credits-for-ai-agent-per-month",
          defaultMessage: "{count} Message credits for AI agent per month",
        },
        { count: formatCredits(aiCredits) }
      ),
      tooltip: t({
        id: "plans-data.features.message-credits-tooltip",
        defaultMessage:
          "Message credits are used by the AI Agent when it sends messages in the channels",
      }),
    },
    {
      name:
        ticketCredits === null
          ? t({
              id: "plans-data.features.unlimited-tickets",
              defaultMessage: "Unlimited Tickets",
            })
          : t(
              {
                id: "plans-data.features.ticket-count",
                defaultMessage: "{count} Tickets",
              },
              { count: ticketCredits }
            ),
    },
    {
      name: t(
        {
          id: "plans-data.features.ai-knowledge-mb",
          defaultMessage:
            "{count} {count, plural, one {MB} other {MB} } of AI Agent Knowledge",
        },
        { count: knowledgeCredits }
      ),
    },
    {
      name: t({
        id: "plans-data.features.chat-integrations",
        defaultMessage: "Channels inbox",
      }),
    },
    {
      name: t({
        id: "plans-data.features.agent-setup",
        defaultMessage: "Agent setup",
      }),
    },
    {
      name: t({
        id: "plans-data.features.phone-calls-by-ai",
        defaultMessage: "Phone Calls by AI",
      }),
      tooltip: t({
        id: "plans-data.features.phone-calls-by-ai-tooltip",
        defaultMessage:
          "Routine phone calls handled by AI ✨ AI Agent uses 4 Message Credits per every 1 minute of conversation",
      }),
      visibleOn: PAID_PLAN_TIERS,
    },
  ];

  return features.filter(
    (feature) =>
      feature.visibleOn === undefined || feature.visibleOn.includes(planTier)
  );
};

export const usePlanFeatures = (
  counts: PlanFeatureCounts,
  planTier: PlanTierIndex
) => {
  const { t } = useTranslations();
  const { userCredits, aiCredits, knowledgeCredits, ticketCredits } = counts;
  return useMemo(
    () =>
      buildPlanFeatures(
        t,
        {
          userCredits,
          aiCredits,
          knowledgeCredits,
          ticketCredits,
        },
        planTier
      ),
    [t, userCredits, aiCredits, knowledgeCredits, ticketCredits, planTier]
  );
};

export const usePlansData = (plans: Plan[]) => {
  const { t } = useTranslations();

  const plansFeatures = useMemo(
    () =>
      plans.map((plan, index) => {
        const userCredits = getAddonCredits(plan, ADDON_ID_USERS) ?? 1;
        const aiCredits = getAddonCredits(plan, ADDON_ID_AI_CREDITS) ?? 0;
        const knowledgeCredits = getAddonCredits(plan, ADDON_ID_KNOWLEDGE) ?? 0;
        const ticketCredits = getAddonCredits(plan, ADDON_ID_TICKETS);

        const isFree = plan.prices.every((p) => p.unit_amount === 0);
        const previousPlanName = index > 0 ? plans[index - 1].name : null;

        return {
          id: plan.id,
          planName: plan.name,
          subtitle: plan.description,
          header: previousPlanName
            ? t(
                {
                  id: "plans-data.everything-in-previous-plan",
                  defaultMessage: "Everything in {planName} +",
                },
                { planName: previousPlanName }
              )
            : t({
                id: "plans-data.plan-includes",
                defaultMessage: "Plan includes:",
              }),
          isFree,
          features: buildPlanFeatures(
            t,
            {
              userCredits,
              aiCredits,
              knowledgeCredits,
              ticketCredits,
            },
            getPlanTierIndex(plan)
          ),
        };
      }),
    [plans, t]
  );

  return { plansFeatures };
};
